import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Collapse,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
} from "@mui/icons-material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { styled } from "@mui/material/styles";

import logoCompanyWhite from "../../../assets/images/yoram-logo-export-white.svg";
import { ADMIN_PREFIX, ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { getDeviceId, getSessionPortal } from "../../../utils/Auth";
import { AUTH_CONTEXT_TYPE } from "../../../utils/authPortal";
import { ClientSurveyIcon } from "../../../utils/Icons";
import {
  canViewAdminModules,
  canViewCompanyModules,
  isAdmin,
  isCompanyOwner,
  isSuperAdmin,
  isSupervisorManger,
  isUser,
  isVisitor,
} from "../../../utils/Permissions";
import messages from "../../../assets/locale/messages";
import {
  getPermissionRequest,
  logoutRequest,
} from "../../../store/Auth/actions";
import "./SideMenu.scss";
import {
  adminPermissionModules,
  companyPermissionModules,
  otherRolesStatus,
} from "../../../utils/Constants";
import { clx } from "../../../utils/Helpers";
import { uiTokens } from "../../../utils/uiTokens";
import SidebarEventsIcon from "../../ui/SVG/Sidebar/EventsIcon";
import ClientsIcon from "../../ui/SVG/Sidebar/ClientsIcon";
import MultipleUsersIcon from "../../ui/SVG/Sidebar/MultipleUsersIcon";
import Settings2Icon from "../../ui/SVG/Sidebar/Settings2Icon";
import SidebarCompanyIcon from "../../ui/SVG/Sidebar/CompanyIcon";
import SidebarLogoutIcon from "../../ui/SVG/Sidebar/LogoutIcon";
// import FinanceIcon from "../../ui/SVG/Sidebar/FinanceIcon";
import UserPlusIcon from "../../ui/SVG/Sidebar/UserPlusIcon";
import LoyalApplicantsIcon from "../../ui/SVG/Sidebar/LoyalApplicantsIcon";
import DashboardIcon from "../../ui/SVG/Sidebar/DashboardIcon";

/** Route fragments for every child under the admin "Requests" group — used
 * to tint the group active and auto-expand it on a direct navigation into
 * one of its sub-pages (e.g. from the dashboard's Quick Actions), not just
 * when the user opens it by hand from the sidebar. */
const REQUESTS_MENU_ROUTE_KEYS = [
  "promotions-requests",
  "companies/registration-requests",
  "publishing-requests",
  "demo-requests",
  "profile-edit-requests",
];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

/**
 * Nested sidebar link; icon is optional (e.g. company settings sub-routes).
 * @typedef {Object} SideMenuChild
 * @property {number} id
 * @property {*} title
 * @property {string} slug
 * @property {string[]} activekeys
 * @property {import('react').ReactElement} [icon]
 * @property {import('react').ReactElement} [iconSelected]
 * @property {string[]} [modules] Permission module names gating the child
 *   (visible when the user can view any of them; omitted = always shown).
 */

/**
 * @typedef {Object} SideMenuItem
 * @property {number} id
 * @property {*} title
 * @property {import('react').ReactElement} icon
 * @property {import('react').ReactElement} iconSelected
 * @property {string} [slug]
 * @property {string[]} [activekeys]
 * @property {boolean} [hasChildren]
 * @property {SideMenuChild[]} [children]
 * @property {string[]} [modules] Company permission module names gating the
 *   item (visible when the user can view any of them; omitted = always shown).
 */

const SideMenu = ({
  detectSideMenuOpen,
  closeMobileSideMenu,
  drawerWidth,
  mobileSideMenuOpen,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const role = login?.role;
  const status = role?.status;
  const id = role?.id;
  const permissions = useSelector((state) => state.auth.permission);
  const { shared } = messages[lang];

  const shellSidebarTokens = {
    ...uiTokens.color.app.shellSidebar,
    ...uiTokens.component.shellSidebar,
    surfaceGradientStart: uiTokens.color.semantic.sidebar["gradient-start"],
    surfaceGradientEnd: uiTokens.color.semantic.sidebar["gradient-end"],
    headerDivider: uiTokens.color.semantic.sidebar["gradient-mid"],
    itemHighlightLeft: uiTokens.color.semantic.sidebar["page-selected-fill-1"],
    itemHighlightMid: uiTokens.color.semantic.sidebar["page-selected-fill-2"],
    itemHighlightEnd: uiTokens.color.semantic.sidebar["page-selected-fill-3"],
    itemHighlightTail: uiTokens.color.semantic.sidebar["page-selected-fill-4"],
    itemTopBorderA: uiTokens.color.semantic.sidebar["page-selected-stroke-1"],
    itemTopBorderB: uiTokens.color.semantic.sidebar["page-selected-stroke-2"],
    itemTopBorderC: uiTokens.color.semantic.sidebar["page-selected-stroke-3"],
    itemTopBorderD: uiTokens.color.semantic.sidebar["page-selected-stroke-4"],
    itemTopBorderE: uiTokens.color.semantic.sidebar["page-selected-stroke-2"],
  };

  const shellSidebarCssVars = {
    "--cp-sidebar-bg-start": shellSidebarTokens.surfaceGradientStart,
    "--cp-sidebar-bg-end": shellSidebarTokens.surfaceGradientEnd,
    "--cp-nav-muted": shellSidebarTokens.navLabelMuted,
    "--cp-header-divider": shellSidebarTokens.headerDivider,
    "--cp-header-underline-width": shellSidebarTokens.headerUnderlineWidth,
    "--cp-header-underline-radius": shellSidebarTokens.headerUnderlineRadius,
    "--cp-item-highlight-left": shellSidebarTokens.itemHighlightLeft,
    "--cp-item-highlight-mid": shellSidebarTokens.itemHighlightMid,
    "--cp-item-highlight-end": shellSidebarTokens.itemHighlightEnd,
    "--cp-item-highlight-tail": shellSidebarTokens.itemHighlightTail,
    "--cp-item-border-a": shellSidebarTokens.itemTopBorderA,
    "--cp-item-border-b": shellSidebarTokens.itemTopBorderB,
    "--cp-item-border-c": shellSidebarTokens.itemTopBorderC,
    "--cp-item-border-d": shellSidebarTokens.itemTopBorderD,
    "--cp-item-border-e": shellSidebarTokens.itemTopBorderE,
    "--cp-nav-logout-hover": shellSidebarTokens.navItemLogoutHoverBg,
  };

  const [open, setOpen] = useState(true);
  const [path, setPath] = useState("");
  const [openItemsIds, setOpenItemsIds] = useState([2]);

  const handleItemClick = (itemId) => {
    setOpenItemsIds((prev) =>
      prev.includes(itemId) ? [] : [itemId],
    );
  };

  const handleMenuLinkClick = () => {
    setOpenItemsIds([]);
  };

  useEffect(() => {
    setPath(location.pathname);
  }, [location, setPath]);

  useEffect(() => {
    if (
      getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY ||
      getSessionPortal() === AUTH_CONTEXT_TYPE.CLIENT ||
      id == null ||
      isSuperAdmin() ||
      isAdmin() ||
      isSupervisorManger()
    ) {
      return;
    }
    dispatch(getPermissionRequest({ id }));
    /** Refetch on navigation too, so a mid-session permission change (granted
     * or revoked server-side) doesn't leave the visitor on a stale matrix. */
  }, [lang, id, dispatch, location.pathname]);

  useEffect(() => {
    detectSideMenuOpen(open);
  }, [detectSideMenuOpen, open]);

  useEffect(() => {
    if (
      getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY &&
      path.includes("/company/settings")
    ) {
      setOpenItemsIds([50]);
    }
  }, [path]);

  // Landing directly on a "Requests" sub-page (e.g. a dashboard Quick
  // Actions link) should show the group expanded, same as clicking it open
  // from the sidebar would — and collapse again once you navigate away from
  // it, so it doesn't stay tinted on an unrelated page (e.g. the
  // review-profile-update-request page, which lives under Companies).
  useEffect(() => {
    const matchesRequests = REQUESTS_MENU_ROUTE_KEYS.some((key) =>
      path.includes(key),
    );

    if (matchesRequests) {
      setOpenItemsIds([3]);
    } else {
      setOpenItemsIds((prev) => (prev.includes(3) ? [] : prev));
    }
  }, [path]);

  const handleSideMenuOpen = () => {
    setOpen(true);
  };

  const handleSideMenuClose = () => {
    setOpen(false);
  };

  const handleLogout = () => {
    dispatch(
      logoutRequest({
        device_id: getDeviceId(),
      }),
    );
  };

  const activeRoute = (activekeys) => {
    return activekeys && activekeys.some((key) => path.includes(key));
  };

  const isCompaniesListMenuActive = (pathname) => {
    if (pathname === ROUTE_PATHS.companiesList) return true;
    const companiesSectionPrefix = `${ADMIN_PREFIX}/companies/`;
    if (!pathname.startsWith(companiesSectionPrefix)) return false;
    if (pathname.startsWith(ROUTE_PATHS.registrationRequests)) return false;
    return true;
  };

  /** @type {SideMenuItem[]} */
  let sideMenuItems = [
    {
      id: 2,
      title: shared.events,
      icon: <SidebarEventsIcon selected={false} width={24} height={24} />,
      iconSelected: <SidebarEventsIcon selected width={24} height={24} />,
      slug: ROUTE_PATHS.events,
      activekeys: ["events"],
      hasChildren: false,
      children: [],
      modules: [adminPermissionModules.events],
    },
    {
      id: 3,
      title: shared.requests,
      icon: <UserPlusIcon selected={false} width={24} height={24} />,
      iconSelected: <UserPlusIcon selected width={24} height={24} />,
      hasChildren: true,
      // Union of every child's activekeys, so the group itself tints as
      // active on any of its sub-pages (not just when the user expands it
      // by hand) — see REQUESTS_MENU_ROUTE_KEYS below for the matching
      // auto-expand effect.
      activekeys: REQUESTS_MENU_ROUTE_KEYS,
      children: [
        {
          id: 3.2,
          title: shared.promotions,
          slug: ROUTE_PATHS.promotionRequests,
          activekeys: ["promotions-requests"],
          modules: [adminPermissionModules.promotions],
        },
      ],
    },
    {
      id: 4,
      title: shared.applicants,
      icon: <LoyalApplicantsIcon selected={false} width={24} height={24} />,
      iconSelected: <LoyalApplicantsIcon selected width={24} height={24} />,
      slug: ROUTE_PATHS.applicants,
      activekeys: ["applicants"],
      hasChildren: false,
      children: [],
      modules: [adminPermissionModules.applicants],
    }
  ];

  if (isVisitor()) {
    if (
      !role ||
      status !== otherRolesStatus.enabled ||
      !Array.isArray(permissions)
    ) {
      sideMenuItems = [];
    } else {
      // Hide modules the visitor's role can't view. Gating is declarative
      // (`modules` per item, matched against the fresh admin permissions matrix
      // via canViewAdminModules) — no locale-dependent title matching.
      sideMenuItems = sideMenuItems
        .map((menuItem) =>
          menuItem.hasChildren
            ? {
                ...menuItem,
                children: menuItem.children.filter((child) =>
                  canViewAdminModules(child.modules),
                ),
              }
            : menuItem,
        )
        .filter((menuItem) =>
          menuItem.hasChildren
            ? menuItem.children.length > 0
            : canViewAdminModules(menuItem.modules),
        );
    }
  }
  if (isSupervisorManger()) {
    // Supervisors only see Events (filtered by supervisor_id on user_app).
    sideMenuItems = [
      {
        id: 2,
        title: shared.events,
        icon: <SidebarEventsIcon selected={false} width={24} height={24} />,
        iconSelected: <SidebarEventsIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.events,
        activekeys: ["events"],
        hasChildren: false,
        children: [],
      },
    ];
  }

  if (isSuperAdmin() && !isUser() && !isSupervisorManger()) {
    sideMenuItems.unshift({
      id: 1,
      title: shared.dashboard,
      icon: <DashboardIcon selected={false} width={24} height={24} />,
      iconSelected: <DashboardIcon selected width={24} height={24} />,
      slug: ROUTE_PATHS.adminDashboard,
      activekeys: ["admin/dashboard"],
      hasChildren: false,
      children: [],
    });

    const eventsIndex = sideMenuItems.findIndex((item) => item.id === 2);
    const insertAt = eventsIndex >= 0 ? eventsIndex + 1 : 1;
    sideMenuItems.splice(insertAt, 0, {
      id: 13,
      title: shared.companiesListMenu,
      icon: <SidebarCompanyIcon selected={false} width={24} height={24} />,
      iconSelected: <SidebarCompanyIcon selected width={24} height={24} />,
      slug: ROUTE_PATHS.companiesList,
      activekeys: [],
      hasChildren: false,
      children: [],
    });

    const requestsIndex = sideMenuItems.findIndex((item) => item.id === 3);
    if (requestsIndex >= 0) {
      sideMenuItems[requestsIndex].children.push({
        id: 3.3,
        title: shared.registrationRequestsMenu,
        slug: ROUTE_PATHS.registrationRequests,
        activekeys: ["companies/registration-requests"],
      });
      sideMenuItems[requestsIndex].children.push({
        id: 3.6,
        title: shared.publishingRequestsMenu,
        slug: ROUTE_PATHS.publishingRequests,
        activekeys: ["publishing-requests"],
      });
      sideMenuItems[requestsIndex].children.push({
        id: 3.4,
        title: shared.demoRequestsMenu,
        slug: ROUTE_PATHS.demoRequests,
        activekeys: ["demo-requests"],
      });
      sideMenuItems[requestsIndex].children.push({
        id: 3.5,
        title: shared.profileEditRequestsMenu,
        slug: ROUTE_PATHS.profileEditRequests,
        activekeys: ["profile-edit-requests"],
      });
    }
  }

  if (isUser()) {
    sideMenuItems = [];
    sideMenuItems.push(
      {
        id: 1,
        title: shared.events,
        icon: <SidebarEventsIcon selected={false} width={24} height={24} />,
        iconSelected: <SidebarEventsIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.events,
        activekeys: ["events"],
        hasChildren: false,
        children: [],
      },
      {
        id: 2,
        title: shared.clientSurvey,
        icon: <ClientSurveyIcon selected={false} width={24} height={24} />,
        iconSelected: <ClientSurveyIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.clientSurvey,
        activekeys: ["client-survey"],
        hasChildren: false,
        children: [],
      },
    );
  }

  if (getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY) {
    sideMenuItems = [
      {
        id: 1,
        title: shared.dashboard,
        icon: <DashboardIcon selected={false} width={24} height={24} />,
        iconSelected: <DashboardIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.companyDashboard,
        activekeys: ["company/dashboard"],
        hasChildren: false,
        children: [],
      },
      {
        id: 2,
        title: shared.events,
        icon: <SidebarEventsIcon selected={false} width={24} height={24} />,
        iconSelected: <SidebarEventsIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.companyEvents,
        activekeys: ["company/events"],
        hasChildren: false,
        children: [],
        modules: [companyPermissionModules.events],
      },
      {
        id: 3,
        title: shared.clientsManagement,
        icon: <ClientsIcon selected={false} width={24} height={24} />,
        iconSelected: <ClientsIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.companyClients,
        activekeys: ["company/clients"],
        hasChildren: false,
        children: [],
        modules: [companyPermissionModules.manageClients],
      },
      {
        id: 51,
        title: shared.companyPortalTeamPermissions,
        icon: <MultipleUsersIcon selected={false} width={24} height={24} />,
        iconSelected: <MultipleUsersIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.companyTeamsPermissions,
        activekeys: ["company/teams-permissions"],
        hasChildren: false,
        children: [],
        modules: [
          companyPermissionModules.employees,
          companyPermissionModules.rolesManagement,
        ],
      },
      {
        id: 52,
        title: shared.companyPortalLoyalApplicants,
        icon: <LoyalApplicantsIcon selected={false} width={24} height={24} />,
        iconSelected: <LoyalApplicantsIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.companyLoyalApplicants,
        activekeys: ["company/loyal-applicants"],
        hasChildren: false,
        children: [],
        modules: [companyPermissionModules.applicants],
      },
      {
        id: 50,
        title: shared.settings,
        icon: <Settings2Icon selected={false} width={24} height={24} />,
        iconSelected: <Settings2Icon selected width={24} height={24} />,
        hasChildren: true,
        activekeys: ["company/settings"],
        children: [
          {
            id: 50.1,
            title: shared.companyPortalProfile,
            slug: ROUTE_PATHS.companySettingsProfile,
            activekeys: ["company/settings/profile"],
          },
          {
            id: 50.2,
            title: shared.companyPortalSecurity,
            slug: ROUTE_PATHS.companySettingsSecurity,
            activekeys: ["company/settings/security"],
          },
        ],
      },
    ];

    // Hide modules the employee's role can't view (owner / sessions without
    // permissions data keep everything — see canViewCompanyModules).
    sideMenuItems = sideMenuItems
      .map((menuItem) =>
        menuItem.hasChildren
          ? {
              ...menuItem,
              children: menuItem.children.filter((child) =>
                canViewCompanyModules(child.modules, login),
              ),
            }
          : menuItem,
      )
      .filter((menuItem) =>
        menuItem.hasChildren
          ? menuItem.children.length > 0
          : canViewCompanyModules(menuItem.modules, login),
      )
      // Dashboard is owner-only (its APIs are) — see `companyOwnerOnly` routes.
      .filter(
        (menuItem) =>
          menuItem.slug !== ROUTE_PATHS.companyDashboard ||
          isCompanyOwner(login),
      );
  }

  if (getSessionPortal() === AUTH_CONTEXT_TYPE.CLIENT) {
    sideMenuItems = [
      // {
      //   id: 1,
      //   title: shared.dashboard,
      //   icon: <DashboardIcon selected={false} width={24} height={24} />,
      //   iconSelected: <DashboardIcon selected width={24} height={24} />,
      //   slug: ROUTE_PATHS.clientPortalDashboard,
      //   activekeys: ["client/dashboard"],
      //   hasChildren: false,
      //   children: [],
      // },
      {
        id: 2,
        title: shared.events,
        icon: <SidebarEventsIcon selected={false} width={24} height={24} />,
        iconSelected: <SidebarEventsIcon selected width={24} height={24} />,
        slug: ROUTE_PATHS.clientPortalEvents,
        activekeys: ["client/events"],
        hasChildren: false,
        children: [],
      },
      // {
      //   id: 3,
      //   title: shared.finance,
      //   icon: <FinanceIcon selected={false} width={24} height={24} />,
      //   iconSelected: <FinanceIcon selected width={24} height={24} />,
      //   slug: ROUTE_PATHS.clientPortalFinance,
      //   activekeys: ["client/finance"],
      //   hasChildren: false,
      //   children: [],
      // },
      {
        id: 50,
        title: shared.settings,
        icon: <Settings2Icon selected={false} width={24} height={24} />,
        iconSelected: <Settings2Icon selected width={24} height={24} />,
        hasChildren: true,
        activekeys: ["client/settings", "client/change-password"],
        children: [
          {
            id: 50.2,
            title: shared.companyPortalSecurity,
            slug: ROUTE_PATHS.clientSettingsSecurity,
            activekeys: ["client/settings/security", "client/change-password"],
          },
        ],
      },
    ];
  }

  const renderSideMenuItems = (device) => {
    const listBtnPaddingSx = {
      py: uiTokens.space[4],
      pl: uiTokens.space[12],
      pr: uiTokens.space[12],
    };

    const baseListItemButtonSx = {
      minHeight: 48,
      justifyContent: open ? "initial" : "center",
      ...listBtnPaddingSx,
    };

    const mainListIconSx = {
      minWidth: 0,
      mr: lang === "en" && open ? uiTokens.space[12] : "auto",
      ml: lang === "ar" && open ? uiTokens.space[12] : "auto",
      justifyContent: "center",
      /** Neutralize MUI ListItemIcon default theme.palette.action.active (forces currentColor into SVG). */
      color: "transparent",
    };

    const headerIconSx = { color: uiTokens.color.semantic.background.default };

    const logoClassName = clx("mb-0 mx-3 logo", "logo--shell-white");

    const submenuChevronSx = {
      color: uiTokens.color.semantic.background.default,
      flexShrink: 0,
    };

    const renderLogoutItem = () => (
      <ListItem
        disablePadding
        sx={{ display: "block" }}
        className={clx(
          "list-item",
          !open && "closed-menu",
          "list-item--shell-logout",
        )}
      >
        <ListItemButton
          sx={baseListItemButtonSx}
          className={clx(
            "list-item-wrapper",
            !open && "closed-menu",
            "list-item-wrapper--logout",
          )}
          onClick={handleLogout}
        >
          <ListItemIcon sx={mainListIconSx}>
            <SidebarLogoutIcon selected={false} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={shared.logout}
            sx={{ display: open ? "block" : "none", textAlign: "start" }}
            className="list-item-label"
          />
        </ListItemButton>
      </ListItem>
    );

    const renderMenuIcon = (isActive, icon, iconSelected) => {
      if (isActive || !iconSelected) return isActive ? iconSelected : icon;

      return (
        <>
          <span className="side-menu-icon side-menu-icon--default">{icon}</span>
          <span className="side-menu-icon side-menu-icon--selected">
            {iconSelected}
          </span>
        </>
      );
    };

    const menuItemsNodes = sideMenuItems.map(
      ({
        id,
        title,
        icon,
        iconSelected,
        slug,
        activekeys,
        hasChildren,
        children,
      }) => {
        const itemActive =
          id === 13
            ? isCompaniesListMenuActive(path)
            : Boolean(activekeys && activeRoute(activekeys));
        const itemExpanded = openItemsIds.includes(id);

        return hasChildren ? (
          <div
            key={id}
            className={clx(
              "side-menu-group",
              itemExpanded && "side-menu-group--expanded",
              itemActive && "side-menu-group--active",
            )}
          >
            <ListItem
              disablePadding
              sx={{ display: "block" }}
              className="list-item"
              onClick={() => handleItemClick(id)}
            >
              <ListItemButton
                sx={baseListItemButtonSx}
                className="list-item-wrapper"
              >
                <ListItemIcon sx={mainListIconSx}>
                  {renderMenuIcon(itemActive, icon, iconSelected)}
                </ListItemIcon>
                <ListItemText
                  primary={title}
                  sx={{
                    display: open ? "block" : "none",
                    textAlign: "start",
                  }}
                  className="list-item-label"
                />
                {itemExpanded ? (
                  <ChevronUp size={16} style={submenuChevronSx} />
                ) : (
                  <ChevronDown size={16} style={submenuChevronSx} />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse
              in={itemExpanded}
              timeout={260}
              className="side-menu-collapse"
            >
              <List
                component="div"
                disablePadding
                className={`child-sub-list ${!open ? "closed-menu" : ""}`}
              >
                {children.map((child, index) => {
                  if (!child) return null;

                  const childActive = Boolean(
                    child.activekeys && activeRoute(child.activekeys),
                  );

                  return (
                    <Link to={child.slug} key={index}>
                      <ListItem
                        selected={childActive}
                        disablePadding
                        sx={{ display: "block" }}
                        className="list-item"
                      >
                        <ListItemButton
                          sx={baseListItemButtonSx}
                          className={`list-item-wrapper ${
                            !open ? "closed-menu" : ""
                          }`}
                        >
                          {(child.icon || child.iconSelected) && (
                            <ListItemIcon
                              sx={{
                                minWidth: 20,
                                mr:
                                  lang === "en" && open
                                    ? uiTokens.space[16]
                                    : "auto",
                                ml:
                                  lang === "ar" && open
                                    ? uiTokens.space[16]
                                    : "auto",
                                justifyContent: "center",
                                color: "transparent",
                              }}
                            >
                              {renderMenuIcon(
                                childActive,
                                child.icon,
                                child.iconSelected,
                              )}
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={child.title}
                            sx={{
                              display: open ? "block" : "none",
                              textAlign: "start",
                            }}
                            className="list-item-label"
                          />
                        </ListItemButton>
                      </ListItem>
                    </Link>
                  );
                })}
              </List>
            </Collapse>
          </div>
        ) : (
          <Link to={slug} key={id} onClick={handleMenuLinkClick}>
            <ListItem
              selected={itemActive}
              disablePadding
              sx={{ display: "block" }}
              className="list-item"
            >
              <ListItemButton
                sx={baseListItemButtonSx}
                className={`list-item-wrapper ${!open ? "closed-menu" : ""}`}
              >
                <ListItemIcon sx={mainListIconSx}>
                  {renderMenuIcon(itemActive, icon, iconSelected)}
                </ListItemIcon>
                <ListItemText
                  primary={title}
                  sx={{
                    display: open ? "block" : "none",
                    textAlign: "start",
                  }}
                  className="list-item-label"
                />
              </ListItemButton>
            </ListItem>
          </Link>
        );
      },
    );

    return (
      <div
        className={clx(
          "side-menu-container",
          "side-menu-container--shell",
        )}
        style={shellSidebarCssVars}
      >
        <DrawerHeader
          className={clx("justify-content-between", "side-menu-header--shell")}
        >
          {device === "mobile" ? (
            <>
              <img
                src={logoCompanyWhite}
                alt="proline-logo"
                className={logoClassName}
              />

              <IconButton
                onClick={closeMobileSideMenu}
                className="close-side-menu-icon p-0"
                sx={headerIconSx}
              >
                <Close />
              </IconButton>
            </>
          ) : (
            <>
              {open && (
                <>
                  <img
                    src={logoCompanyWhite}
                    alt="proline-logo"
                    className={logoClassName}
                  />
                </>
              )}
              <IconButton
                onClick={open ? handleSideMenuClose : handleSideMenuOpen}
                className="side-menu-toggle-icon"
                sx={headerIconSx}
              >
                {lang === "en" ? (
                  open ? (
                    <ChevronLeft />
                  ) : (
                    <ChevronRight />
                  )
                ) : open ? (
                  <ChevronRight />
                ) : (
                  <ChevronLeft />
                )}
              </IconButton>
            </>
          )}
        </DrawerHeader>
        <div className="side-menu-body side-menu-body--shell">
          <List className="side-menu-list side-menu-list--shell-main">
            {menuItemsNodes}
          </List>
          <List className="side-menu-list side-menu-list--shell-footer">
            {renderLogoutItem()}
          </List>
        </div>
      </div>
    );
  };

  const desktopMenuCustomStyle = {
    // Permanent sidebar is desktop-only (lg = 1024px); tablet uses the temporary
    // drawer behind the navbar hamburger.
    display: { xs: "none", lg: "block" },
    "& .MuiDrawer-paper": {
      position: "relative",
      whiteSpace: "nowrap",
      width: drawerWidth,
      transition: (theme) =>
        theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      boxSizing: "border-box",
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      ...(!open && {
        overflowX: "hidden",
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        width: (theme) => ({
          xs: theme.spacing(7),
          sm: theme.spacing(9),
        }),
      }),
    },
  };

  const mobileMenuCustomStyle = {
    display: { xs: "block", lg: "none" },
    "& .MuiDrawer-paper": {
      boxSizing: "border-box",
      width: drawerWidth,
      backgroundColor: "transparent",
      boxShadow: "none",
    },
  };

  return (
    <Box
      component="nav"
      aria-label="mailbox folders"
      className="side-menu position-fixed"
    >
      <Drawer
        variant="temporary"
        open={mobileSideMenuOpen}
        onClose={closeMobileSideMenu}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={mobileMenuCustomStyle}
        className="mobile-side-menu"
        anchor={lang === "en" ? "left" : "right"}
      >
        {renderSideMenuItems("mobile")}
      </Drawer>
      <Drawer
        variant="permanent"
        open={open}
        sx={desktopMenuCustomStyle}
        className="desktop-side-menu"
      >
        {renderSideMenuItems("desktop")}
      </Drawer>
    </Box>
  );
};

SideMenu.propTypes = {
  detectSideMenuOpen: PropTypes.func.isRequired,
  closeMobileSideMenu: PropTypes.func.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  mobileSideMenuOpen: PropTypes.bool.isRequired,
};

export default SideMenu;
